import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Briefcase, Building2, Mail, Search, Ticket, User, X } from 'lucide-react';
import { searchApi, type SearchEntity, type SearchHit } from '../api/search';
import { useCompany } from '../contexts/CompanyContext';
import { relativeTime } from '../lib/crmInbox';

// One box over tickets, people, accounts, opportunities and conversations.
// Opens on Ctrl/⌘+K from anywhere, walks with the arrow keys, opens with Enter.
const ICON: Record<SearchEntity, React.ReactNode> = {
  ticket: <Ticket className="w-3.5 h-3.5" />,
  contact: <User className="w-3.5 h-3.5" />,
  organization: <Building2 className="w-3.5 h-3.5" />,
  deal: <Briefcase className="w-3.5 h-3.5" />,
  conversation: <Mail className="w-3.5 h-3.5" />,
};

const TINT: Record<SearchEntity, string> = {
  ticket: 'var(--scpa-accent)',
  contact: 'var(--scpa-text-muted)',
  organization: 'var(--scpa-primary)',
  deal: 'var(--scpa-status-warning)',
  conversation: 'var(--scpa-primary)',
};

export default function GlobalSearch() {
  const navigate = useNavigate();
  const { currentCompany } = useCompany();
  const cid = (currentCompany as any)?.id ?? (currentCompany as any)?.uuid;

  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Ctrl/⌘+K from anywhere, Escape to leave.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(v => !v);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!open) { setQ(''); setDebounced(''); setCursor(0); return; }
    const t = setTimeout(() => inputRef.current?.focus(), 30);
    return () => clearTimeout(t);
  }, [open]);

  // Typing is cheap; querying is not. Wait for a pause.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 180);
    return () => clearTimeout(t);
  }, [q]);

  const { data, isFetching } = useQuery({
    queryKey: ['global-search', cid, debounced],
    queryFn: () => searchApi.search(debounced),
    enabled: open && debounced.length >= 2 && !!cid,
  });

  const flat: SearchHit[] = (data?.groups ?? []).flatMap(g => g.hits);
  useEffect(() => { setCursor(0); }, [debounced]);

  const go = (hit: SearchHit) => { setOpen(false); navigate(hit.link); };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { setOpen(false); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor(c => Math.min(c + 1, flat.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setCursor(c => Math.max(c - 1, 0)); }
    if (e.key === 'Enter' && flat[cursor]) { e.preventDefault(); go(flat[cursor]); }
  };

  let index = -1;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="scpa-ghost-btn"
        title="Search everything (Ctrl+K)"
        aria-label="Search everything"
      >
        <Search className="w-4 h-4" />
      </button>

      {open && createPortal(
        <div
          className="scpa-scrim"
          role="dialog"
          aria-modal="true"
          aria-label="Search everything"
          onClick={() => setOpen(false)}
          style={{ alignItems: 'flex-start', paddingTop: '12vh' }}
        >
          <div
            className="scpa-modal"
            style={{ width: 'min(640px, 100%)', maxHeight: '70vh', display: 'flex', flexDirection: 'column' }}
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 px-4 py-3 border-b border-line">
              <Search className="w-4 h-4 text-muted flex-shrink-0" />
              <input
                ref={inputRef}
                className="flex-1 bg-transparent text-fg outline-none text-sm"
                placeholder="Search tickets, people, accounts, opportunities, conversations…"
                value={q}
                onChange={e => setQ(e.target.value)}
                onKeyDown={onKeyDown}
              />
              <button type="button" onClick={() => setOpen(false)} className="text-muted hover:text-fg" aria-label="Close">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="overflow-y-auto flex-1">
              {debounced.length < 2 ? (
                <p className="text-sm text-muted px-4 py-6 text-center">
                  Type at least two characters. Ticket numbers, names, email addresses and CRM numbers all work.
                </p>
              ) : isFetching && !data ? (
                <p className="text-sm text-muted px-4 py-6 text-center">Searching…</p>
              ) : flat.length === 0 ? (
                <p className="text-sm text-muted px-4 py-6 text-center">Nothing matches “{debounced}”.</p>
              ) : (
                (data?.groups ?? []).map(group => (
                  <div key={group.type}>
                    <p className="text-[11px] font-bold uppercase tracking-wide text-muted px-4 pt-3 pb-1">{group.label}</p>
                    <ul>
                      {group.hits.map(hit => {
                        index += 1;
                        const active = index === cursor;
                        const myIndex = index;
                        return (
                          <li key={`${hit.type}:${hit.id}`}>
                            <button
                              type="button"
                              onMouseEnter={() => setCursor(myIndex)}
                              onClick={() => go(hit)}
                              className={`w-full text-left px-4 py-2 flex items-start gap-2.5 ${active ? 'bg-row-odd' : ''}`}
                            >
                              <span
                                className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5"
                                style={{ color: TINT[hit.type], backgroundColor: `color-mix(in srgb, ${TINT[hit.type]} 14%, transparent)` }}
                                aria-hidden
                              >
                                {ICON[hit.type]}
                              </span>
                              <span className="flex-1 min-w-0">
                                <span className="block text-sm text-fg truncate">
                                  {hit.ref && <span className="font-mono text-[11px] text-muted mr-1.5">{hit.ref}</span>}
                                  {hit.title}
                                </span>
                                {hit.subtitle && <span className="block text-xs text-muted truncate">{hit.subtitle}</span>}
                              </span>
                              {hit.at && <span className="text-[11px] text-muted whitespace-nowrap mt-0.5">{relativeTime(hit.at)}</span>}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))
              )}
            </div>

            <div className="px-4 py-2 border-t border-line text-[11px] text-muted flex gap-4">
              <span>↑↓ move</span><span>↵ open</span><span>esc close</span>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
